import { PnlTimePeriodOption } from 'src/@types/performance';
import {
	ONE_DAY_MS,
	ONE_HOUR_MS,
	ONE_MINUTE_MS,
} from 'src/constants/constants';

/**
 * Prints the amount of days, hours, or minutes from the current time to the searchDate.
 * For example "3 hours" or "25 minutes" (from now).
 * Switches from days to hours to minutes when the diff goes below 1 of that unit.
 */
export const findDaysHoursMinutesFromNow = (
	searchDate: Date,
	shortform = false,
	finishedWording = 'Now'
): string => {
	const timeDiff = Math.max(searchDate.getTime() - Date.now(), 0);
	const dateString = [];

	if (timeDiff === 0) return finishedWording;

	const daysDiffRound = Math.round(timeDiff / ONE_DAY_MS);
	const daysDiffFloor = Math.floor(timeDiff / ONE_DAY_MS);
	if (daysDiffRound > 0 && daysDiffFloor > 0) {
		if (shortform) {
			dateString.push(`${daysDiffFloor}d`);
		} else {
			dateString.push(daysDiffFloor === 1 ? '1 day' : `${daysDiffFloor} days`);
		}
	}

	const timeDiffModDay = timeDiff % ONE_DAY_MS;
	const hoursDiffRound = Math.round(timeDiffModDay / ONE_HOUR_MS);
	const hoursDiffFloor = Math.floor(timeDiffModDay / ONE_HOUR_MS);
	if (hoursDiffRound > 0 && hoursDiffFloor > 0) {
		if (shortform) {
			dateString.push(hoursDiffFloor === 1 ? '1hr' : `${hoursDiffFloor}hrs`);
		} else {
			dateString.push(
				hoursDiffFloor === 1 ? '1 hour' : `${hoursDiffFloor} hours`
			);
		}
	}

	const timeDiffModHour = timeDiffModDay % ONE_HOUR_MS;
	const minutesDiffRound = Math.round(timeDiffModHour / ONE_MINUTE_MS);
	const minutesDiffFloor = Math.floor(timeDiffModHour / ONE_MINUTE_MS);
	if (minutesDiffRound > 0 && minutesDiffFloor > 0 && daysDiffFloor === 0) {
		if (shortform) {
			dateString.push(
				minutesDiffFloor === 1 ? '1min' : `${minutesDiffFloor}mins`
			);
		} else {
			dateString.push(
				minutesDiffFloor === 1 ? '1 minute' : `${minutesDiffFloor} minutes`
			);
		}
	}

	if (!dateString.length) {
		return '<1 minute';
	}

	return dateString.join(' ');
};

export const findDaysOrHoursAgo = (searchDate: Date, unit: string): number => {
	const divisor = 1000 * 3600 * (unit === 'days' ? 24 : 1);
	const timeDiff = new Date(Date.now()).getTime() - searchDate.getTime();
	return Math.floor(timeDiff / divisor);
};

export const nowTs = () => Math.floor(Date.now() / 1000);

export const timeAgo = (date: Date): number => {
	const now = Date.now();
	const timeAgo = now - date.getTime();
	return timeAgo;
};

export const timeAgoPreciseString = (date: Date): string => {
	const seconds = Math.floor((Date.now() - +date) / 1000);

	let interval = seconds / 31536000;
	if (interval > 1) {
		return interval.toFixed(2) + ' years';
	}

	interval = seconds / 2592000;
	if (interval > 1) {
		return interval.toFixed(2) + ' months';
	}

	interval = seconds / 604800;
	if (interval > 1) {
		return interval.toFixed(2) + ' weeks';
	}

	interval = seconds / 86400;
	if (interval > 1) {
		return interval.toFixed(2) + ' days';
	}

	interval = seconds / 3600;
	if (interval > 1) {
		return interval.toFixed(2) + ' hours';
	}

	interval = seconds / 60;
	if (interval > 1) {
		return interval.toFixed(2) + ' minutes';
	}

	return Math.floor(seconds) + ' seconds';
};

export const periodTimeSize = (unit: PnlTimePeriodOption): number => {
	const hr = 1000 * 60 * 60;
	switch (unit) {
		case '24h':
			return hr * 24;
		case '7d':
			return hr * 24 * 7;
		case '30d':
			return hr * 24 * 30;
		case '90d':
			return hr * 24 * 90;
		case '6m':
			return hr * 24 * 182.125;
		case 'all':
			return Infinity;
	}
};

export const periodOffset = (unit: PnlTimePeriodOption): number => {
	const hr = 1000 * 60 * 60;
	const now = Date.now();

	switch (unit) {
		case '24h':
			return now % hr;
		case '7d':
			return now % (hr * 24);
		case '30d':
		case '90d':
			return now % (hr * 24);
		case '6m':
			return now % (hr * 24);
		case 'all':
			return Infinity;
	}
};

export const printDate = (date: Date) => {
	return date.toLocaleString(undefined, {
		hour12: true,
		timeStyle: 'short',
		dateStyle: 'short',
	});
};

export async function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
